import {map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from '../abstract-control';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import {Observable} from 'rxjs';
import {CacheService} from '../../../../cache/cache.service';
import {LanguageService} from '../../../../language/language.service';
import {ResourceService} from '../../../resource.service';

@Component({
  selector: 'ctrl-syncrule-parameter',
  styleUrls: ['./ctrl-synchronization.scss'],
  template: `
    <table class="table mat-elevation-z8">
      <thead>
        <tr>
          <th>
            <language key="CtrlSyncruleParameterColumnName" defaultValue="Name"></language>
          </th>
          <th>
            <language key="CtrlSyncruleParameterColumnDataType" defaultValue="Data type"></language>
          </th>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        @for (entry of formControl?.value; track entry; let i = $index) {
          <tr class="entry">
            <td>
              <mat-form-field>
                <input
                  matInput
                  [(ngModel)]="entry.name"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  (change)="formControl.updateValueAndValidity()"
                />
              </mat-form-field>
            </td>
            <td>
              <mat-form-field>
                <mat-select
                  [(ngModel)]="entry.type"
                  (selectionChange)="formControl.updateValueAndValidity()"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  [placeholder]="operatorPlaceholder | async"
                >
                  <mat-option value="Boolean">
                    <language
                      key="CtrlSyncruleParameterDataTypeBoolean"
                      defaultValue="Boolean"
                    ></language>
                  </mat-option>
                  <mat-option value="Integer">
                    <language
                      key="CtrlSyncruleParameterDataTypeInteger"
                      defaultValue="Integer"
                    ></language>
                  </mat-option>
                  <mat-option value="String">
                    <language
                      key="CtrlSyncruleParameterDataTypeString"
                      defaultValue="String"
                    ></language>
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </td>
            <td class="buttons">
              <button
                mat-icon-button
                (click)="delete(i)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
              >
                <mat-icon aria-label="Delete">close</mat-icon>
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlSyncruleParameterComponent extends AbstractControlComponent {
  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private cacheService: CacheService,
    private resourceService: ResourceService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
    this.attributePlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'SynchronizationScopeAttributePlaceholder',
          'Please select an attribute.',
          lr,
        );
      }),
    );
    this.operatorPlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'SynchronizationScopeOperatorPlaceholder',
          'Please select an item.',
          lr,
        );
      }),
    );
  }

  attributePlaceholder: Observable<string>;
  operatorPlaceholder: Observable<string>;
  attributes: Observable<string[]>;

  protected formatSource(source: string): SyncParameter[] {
    if (!Array.isArray(source) || source.length === 0) {
      return [new SyncParameter()];
    }

    const parameters: SyncParameter[] = [];
    source.forEach(xml => {
      const sp = SyncParameter.fromXML(xml);
      if (sp != null) {
        parameters.push(sp);
      }
    });

    if (parameters.length === 0) {
      parameters.push(new SyncParameter());
    }

    return parameters;
  }

  protected formatTarget(value: SyncParameter[]): string[] {
    if (!Array.isArray(value)) {
      return null; // TODO: is the empty value null or []?
    }

    value = value.filter((sp: SyncParameter) => sp.name != null && sp.type != null);
    if (value.length === 0) {
      return null; // TODO: is the empty value null or []?
    }

    return value.map((sp: SyncParameter) => sp.asXML());
  }

  delete(index: number): void {
    this.formControl.value.splice(index, 1);
    this.formControl.updateValueAndValidity();
  }

  addRow(): void {
    // don't fire event - unnecessary
    if (!Array.isArray(this.formControl.value)) {
      this.formControl.setValue([new SyncParameter()], {emitEvent: false});
    } else {
      this.formControl.value.push(new SyncParameter());
    }
  }
}

class SyncParameter {
  name: string;
  type: null | 'Boolean' | 'Integer' | 'String';

  constructor(name?: string, type?: 'Boolean' | 'Integer' | 'String' | null) {
    this.name = name;
    this.type = type;
  }

  public asXML(): string {
    let name = this.name ? '' + this.name : '';
    name = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const type = this.type ? '' + this.type : '';
    return '<sync-parameter><name>' + name + '</name><type>' + type + '</type></sync-parameter>';
  }

  public static fromXML(xml: string): SyncParameter {
    if (xml == null || xml.trim() === '') return null;

    const regex =
      /^\s*<sync-parameter>\s*<name>([^<]*)<\/name>\s*<type>([^<]*)<\/type>\s*<\/sync-parameter>\s*$/;

    const matches = xml.match(regex);
    if (matches == null || matches.length !== 3) {
      return null;
    } else {
      let name = matches[1] ? matches[1] : '';
      name = name.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      let type: 'Boolean' | 'Integer' | 'String' | null = null;
      switch (matches[2].trim().toLowerCase()) {
        case 'boolean':
          type = 'Boolean';
          break;
        case 'integer':
          type = 'Integer';
          break;
        case 'string':
          type = 'String';
          break;
      }
      return new SyncParameter(name, type);
    }
  }
}
